import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Spinner, Table } from '@agconnections/grow-ui';
import EllipseMenu from 'components/EllipseMenu';
import RecordsCreatedIndicators from 'components/RecordsCreatedIndicators';
import { buildRecordIndicators } from 'components/RecordsCreatedIndicators/helpers';
import TileTagList from 'components/TileCard/components/TileTagList';
import { resolveTileTagsByIds } from 'components/TileCard/helpers/tileTags';
import { handleRecommendationsMenuOptions } from 'utilities/menus';
import { paths } from 'routes/paths';
import useOperationFlags from 'screens/Applications&WorkOrders/Hooks/useOperationFlags';
import { getSeasons, getSubtitle } from '../../../../helpers/list';
import RECOMMENDATION_TYPE, {
  RECOMMENDATION_TYPE_DISPLAY_NAME
} from '../../../../helpers/recommendationType';

function RecommendationTableRow({
  recommendation,
  cropSeasons,
  tags,
  onAction,
  runningProcesses = false
}) {
  const { feApplicationsActive, feWorkOrdersActive } = useOperationFlags();

  const handleAction = action => onAction(action);
  const flags = resolveTileTagsByIds(recommendation.flagIds, tags);
  if (recommendation?.taskCreated) {
    flags.unshift({
      id: 'task-created',
      name: 'Task Created',
      color: '#73DB78' // cropwise-green
    });
  }
  return (
    <Table.Row>
      <Table.Cell>
        <div className="flex flex-col items-start w-full min-w-0">
          <span className="text-sm font-bold">
            <Link to={`${paths.recommendations}/${recommendation.id}/view`}>
              {recommendation.title}
            </Link>
          </span>
          <TileTagList tags={flags} />
        </div>
      </Table.Cell>
      <Table.Cell>{recommendation.cropZones.length}</Table.Cell>
      <Table.Cell>
        {recommendation.cropZones
          .reduce((acc, zone) => {
            return acc + zone?.areaValue;
          }, 0)
          .toFixed(2)}
      </Table.Cell>
      <Table.Cell>
        {recommendation.recommendationProductEntries.length}
      </Table.Cell>
      <Table.Cell>{getSubtitle(recommendation)}</Table.Cell>
      <Table.Cell>
        {getSeasons({
          seasons: cropSeasons,
          seasonIds: recommendation.cropSeasonIds
        })}
      </Table.Cell>
      <Table.Cell>
        <div className="w-full flex justify-start">
          <RecordsCreatedIndicators
            indicators={buildRecordIndicators(recommendation.childrenRecords)}
          />
        </div>
      </Table.Cell>
      {/* TODO: Add logic for showing agrian status */}
      <Table.Cell>
        {recommendation.type === RECOMMENDATION_TYPE.agrian ? (
          <div className="flex flex-col justify-center items-start">
            <span>
              {RECOMMENDATION_TYPE_DISPLAY_NAME[RECOMMENDATION_TYPE.agrian]}
            </span>
            <span className="text-chili-red">Unresolved</span>
          </div>
        ) : null}
      </Table.Cell>
      <Table.Cell>
        <div
          data-testid="recommendation-table-options-menu"
          className="absolute right-0 flex-none w-10 mr-2"
        >
          {runningProcesses ? (
            <Spinner size="sm" />
          ) : (
            <EllipseMenu
              onAction={handleAction}
              options={handleRecommendationsMenuOptions(
                feApplicationsActive,
                feWorkOrdersActive
              )}
            />
          )}
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

RecommendationTableRow.propTypes = {
  recommendation: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    cropZones: PropTypes.arrayOf(
      PropTypes.shape({
        areaValue: PropTypes.number
      })
    ),
    recommendationProductEntries: PropTypes.arrayOf(PropTypes.object),
    cropSeasonIds: PropTypes.arrayOf(PropTypes.string),
    expirationDate: PropTypes.string,
    proposedDate: PropTypes.string,
    createdOn: PropTypes.string,
    flagIds: PropTypes.arrayOf(PropTypes.string),
    type: PropTypes.string,
    taskCreated: PropTypes.bool,
    childrenRecords: PropTypes.shape({
      countPerRecordType: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          recordType: PropTypes.string
        })
      )
    })
  }).isRequired,
  cropSeasons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ).isRequired,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired
    })
  ).isRequired,
  onAction: PropTypes.func.isRequired,
  runningProcesses: PropTypes.bool
};

export default RecommendationTableRow;
